import { FC } from "react";

import { Stack } from "@mui/material";
import { FormCategory, ShareFormEmbedImageUrl, Locales } from "@simplyk/common";

import { CloseDialogButton } from "./CloseDialogButton";
import { EmbedInstructionsIllustration } from "./EmbedInstructionsIllustration";
import { ShareFormTabLayout } from "./ShareFormTabLayout";
import { ShareFormTabTitle } from "./ShareFormTabTitle";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TextFieldWithCopy } from "@/components/TextFieldWithCopy/TextFieldWithCopy";
import { DashboardButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardDivider } from "@/dashboard-design-system/Divider";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ChevronLeft, ExternalLink } from "@/icons/outlined";

export type SimpleEmbedInstructionsTabProps = {
  embedType: "Form" | "Thermometer" | "Leaderboard" | "OrgPage";
  formCategory?: FormCategory;
  onClose?: () => void;
  onBackClick?: () => void;
  guideLinks?: Record<Locales, string>;
  embedCode: string;
};

export const SimpleEmbedInstructionsTab: FC<SimpleEmbedInstructionsTabProps> = ({
  embedType,
  embedCode,
  formCategory,
  onClose,
  onBackClick,
  guideLinks,
}) => {
  const { t } = useTranslate();
  const { locale } = useLocaleContext();
  const { isSmallScreen } = useMediaQuery();

  return (
    <ShareFormTabLayout
      slots={{
        closeButton: <CloseDialogButton onClick={onClose} />,
        header: <ShareFormTabTitle>{t("common", "shareFormModal.embed.choice.title")}</ShareFormTabTitle>,
        content: (
          <Stack gap={3}>
            <EmbedInstructionsIllustration
              src={ShareFormEmbedImageUrl}
              illustrationWidth={358}
              scale={isSmallScreen ? 0.75 : 1}
              captions={[
                t("common", "shareFormModal.embed.illustration.htmlBlock"),
                t("common", "shareFormModal.embed.illustration.adjustSizing"),
              ]}
            />
            <DashboardDivider />
            <Stack gap={1}>
              <DashboardTypography variant="subtitle2">
                {t("common", "shareFormModal.embed.choice.label")}
              </DashboardTypography>
              <TextFieldWithCopy
                button
                value={embedCode}
                size="medium"
                buttonCopyProps={{ vibe: "brand", variant: "filled", size: "medium" }}
                copyAmplitudeEvent={AmplitudeEvents.ShareFormModalClickCopyEmbed}
                copyAmplitudePayload={{ form_embed_type: embedType, form_type: formCategory }}
              />
            </Stack>
            {guideLinks && (
              <Stack gap={1} sx={{ alignSelf: "center" }}>
                <DashboardTypography variant="subtitle2">
                  {t("common", "shareFormModal.embed.choice.extraHelp")}
                </DashboardTypography>
                <DashboardButton
                  vibe="brand"
                  variant="outlined"
                  size="medium"
                  endIcon={<ExternalLink />}
                  href={guideLinks[locale]}
                  target="_blank"
                >
                  {t("common", "shareFormModal.embed.choice.visitOurGuide")}
                </DashboardButton>
              </Stack>
            )}
          </Stack>
        ),
        footer: (
          <Stack direction="row" alignItems="center" justifyContent="flex-start">
            <DashboardButton
              vibe="neutral"
              variant="outlined"
              size="large"
              startIcon={<ChevronLeft />}
              onClick={onBackClick}
            >
              {t("common", "back")}
            </DashboardButton>
          </Stack>
        ),
      }}
    />
  );
};
